import { useEffect, useState } from 'react';
import { Card, Button, Typography, Tag, Popover, Spin } from 'antd';
import {
  CheckCircleOutlined,
  LockOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { FronteggContext } from '@frontegg/rest-api';
import ZoomIcon from 'src/assets/zoom.svg';
import { useSelector } from 'react-redux';
import { UpgradeButton } from '../../ambient_solo/components/Payments/UpgradeButton';
import ZoomGuidedTour from './ZoomGuidedTour';
import { AxiosAmbient } from 'src/lib';
import { analytics } from 'src/services';
import { useAuth } from '@frontegg/react';

const { Title, Paragraph } = Typography;

const ZoomIntegrationTile = ({ schedulepage = false }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [activePopover, setActivePopover] = useState(null);
  const oAuthDevClientId = import.meta.env.VITE_ZOOM_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_ZOOM_REDIRECT_URI;

  const { currentPractitionerEntitlements } = useSelector(
    (state) => state?.practitionerState
  );
  const isAuraPro = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type == 'pro'
  );
  const { user } = useAuth();

  const toggleConnection = async () => {
    if (isConnected) {
      setIsDisconnecting(true);
      try {
        // Track zoom disconnection
        analytics.track('Disconnected Zoom Integration', {
          email: user?.email,
        });

        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
        };

        await AxiosAmbient.post('/zoom-disconnect', {}, { headers });

        setIsConnected(false);
      } catch (error) {
        console.error('Error disconnecting Zoom:', error);
        setIsConnected(false);
      } finally {
        setIsDisconnecting(false);
      }
    }
  };

  const handleZoomScope = () => {
    // Track zoom connection attempt
    analytics.track('Initiated Zoom Connection', {
      email: user?.email,
    });

    const authUrl = `https://zoom.us/oauth/authorize?response_type=code&client_id=${oAuthDevClientId}&redirect_uri=${encodeURIComponent(redirectUri)}`;

    window.location.href = authUrl;
  };
  const handleShowUpgradeTooltip = (element) => {
    if (!isAuraPro) {
      setActivePopover(element);
    }
  };
  const handleHideUpgradeTooltip = () => {
    setActivePopover(null);
  };

  const handleConnectZoom = () => {
    handleZoomScope();
  };
  // Check backend for Zoom connection status on component mount
  useEffect(() => {
    const checkZoomConnectionStatus = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
        };

        const result = await AxiosAmbient.get('/zoom-connection-status', {
          headers,
        });

        const response = result?.data;
        console.log('Zoom connection status:', response);

        // Update connection status based on backend response
        setIsConnected(response?.connected === true);
      } catch (error) {
        console.error('Failed to check zoom connection:', error);
        setIsConnected(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkZoomConnectionStatus();
  }, []);
  return (
    <div className={`flex flex-col ${schedulepage ? '' : 'page-wrapper'} past`}>
      <Title level={4} className="mb-5 mr-2">
        Available with your plan
      </Title>

      <div className="grid gap-5 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
        <Card
          className={`rounded-lg h-full relative ${
            isConnected ? 'bg-green-50' : 'bg-white'
          }`}
          bodyStyle={{ padding: 0 }}
        >
          {isLoading ? (
            <Tag
              icon={<LoadingOutlined />}
              color="processing"
              className="absolute top-3 right-3 border-none bg-transparent px-0 py-0"
            >
              Checking...
            </Tag>
          ) : isConnected ? (
            <Tag
              icon={<CheckCircleOutlined />}
              color="success"
              className="absolute top-3 right-3 border-none bg-transparent px-0 py-0"
            >
              Connected
            </Tag>
          ) : null}

          <div className="p-5 flex flex-col gap-4 w-full">
            <img src={ZoomIcon} height={30} width={30} className="mr-2" />

            <div className="flex flex-col gap-1">
              <Title level={5} className="!text-base">
                Zoom
              </Title>

              <Paragraph className="text-gray-600 ">
                Allow bot to join and start the cloud recording for Zoom
                encounter.
              </Paragraph>

              <div className="bg-[#fdf7e6] px-2.5 py-1.5 text-gray-600 rounded mt-2">
                Requires Zoom`s <strong>Pro</strong> subscription
              </div>
            </div>

            <div>
              <ZoomGuidedTour />

              {isLoading ? (
                <div className="flex justify-center items-center mt-4">
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 16 }} spin />
                    }
                    size="small"
                  />
                  <span className="ml-2 text-gray-500">
                    Checking connection...
                  </span>
                </div>
              ) : isConnected ? (
                <div className="flex justify-end items-center mt-3">
                  <Button
                    type="text"
                    danger
                    onClick={toggleConnection}
                    loading={isDisconnecting}
                    disabled={isDisconnecting}
                  >
                    {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                  </Button>
                </div>
              ) : (
                <Popover
                  content={
                    <div
                      onMouseEnter={() => handleShowUpgradeTooltip('select')}
                      onMouseLeave={handleHideUpgradeTooltip}
                    >
                      <UpgradeButton
                        text="Upgrade to access this feature"
                        analyticsEvent="Clicked Zoom Integration Accees Button"
                      />
                    </div>
                  }
                  open={!isAuraPro && activePopover === 'select'}
                  trigger="hover"
                  placement="rightTop"
                  mouseLeaveDelay={0.3}
                  overlayInnerStyle={{ padding: '0px' }}
                  align={{
                    offset: [0, 0],
                  }}
                  onOpenChange={(visible) => {
                    if (!visible) {
                      handleHideUpgradeTooltip();
                    }
                  }}
                >
                  <div className="mt-3 w-full flex justify-end items-center">
                    <Button
                      type="primary"
                      onClick={
                        !isAuraPro
                          ? () => handleShowUpgradeTooltip('select')
                          : handleConnectZoom
                      }
                      className=""
                      icon={!isAuraPro && <LockOutlined />}
                      onMouseEnter={() => handleShowUpgradeTooltip('select')}
                      onMouseLeave={handleHideUpgradeTooltip}
                      disabled={isLoading}
                    >
                      {isAuraPro ? 'Connect Zoom' : 'Upgrade to Pro'}
                    </Button>
                  </div>
                </Popover>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ZoomIntegrationTile;
